import { find, isEqual, isFunction, omit } from 'lodash';

import { getMetaContent } from 'components/utils/html';
import {
  isCustomDomainPage,
  isJobBoardDomainPage,
} from 'containers/App/utils/helpers';
import { storedTracking } from 'containers/PublicJobBoard/utils/tracking';

const DEFINITION_KEYS = [
  'regex',
  'condition',
  'params',
  'public',
  'minDuration',
];

const SHARED_LIST_VIEW = {
  type: 'view',
  source_type: 'shared_list',
  minDuration: 0.1,
};

const JOB_BOARD_VIEW = {
  type: 'view',
  source_type: 'job_board',
  public: true,
  minDuration: 2,
};

const isPlatformJobBoardPage = () => !isJobBoardDomainPage();

const jobBoardDomainParams = () => ({
  company_id: getMetaContent("name='gustav:job-board-company-id'"),
  source_slug: getMetaContent("name='gustav:job-board-slug'"),
});

const visitorId = ({ company_id, source_slug }) =>
  storedTracking(company_id, source_slug).visitor_id;

/* eslint security/detect-unsafe-regex:0 */
export const EVENTS = [
  {
    regex: /^\/cp\/[a-zA-Z0-9]{22}\/(?<source_id>[a-zA-Z0-9]{22})\/(candidate|requests)\/(?<target_id>[a-zA-Z0-9]{22})(\/change-stage)?$/,
    ...SHARED_LIST_VIEW,
    target_type: 'candidate',
  },
  {
    regex: /^\/(?<source_id>[a-zA-Z0-9]{22})\/(candidate|requests)\/(?<target_id>[a-zA-Z0-9]{22})(\/change-stage)?$/,
    ...SHARED_LIST_VIEW,
    target_type: 'candidate',
    condition: isCustomDomainPage,
  },
  {
    regex: /^\/cp\/[a-zA-Z0-9]{22}\/(?<source_id>[a-zA-Z0-9]{22})(\/requests)?$/,
    ...SHARED_LIST_VIEW,
    target_type: null,
  },
  {
    regex: /^\/(?<source_id>[a-zA-Z0-9]{22})(\/requests)?$/,
    ...SHARED_LIST_VIEW,
    target_type: null,
    condition: isCustomDomainPage,
  },
  {
    regex: /^\/jobs\/(?<company_id>[a-zA-Z0-9]{22})\/(?<source_slug>[^/]+)(\/jobs)?\/?$/,
    ...JOB_BOARD_VIEW,
    target_type: null,
    condition: isPlatformJobBoardPage,
  },
  {
    regex: /^\/jobs\/(?<company_id>[a-zA-Z0-9]{22})\/(?<source_slug>[^/]+)\/(?<target_slug>(?!jobs$|saved$|portal$)[^/]+)\/?$/,
    ...JOB_BOARD_VIEW,
    target_type: 'job',
    condition: isPlatformJobBoardPage,
  },
  {
    regex: /^\/jobs\/(?<company_id>[a-zA-Z0-9]{22})\/(?<source_slug>[^/]+)\/(?<target_slug>[^/]+)\/apply\/?$/,
    ...JOB_BOARD_VIEW,
    type: 'apply',
    target_type: 'job',
    condition: isPlatformJobBoardPage,
  },
  {
    regex: /^\/jobs\/?$/,
    ...JOB_BOARD_VIEW,
    target_type: null,
    condition: isJobBoardDomainPage,
    params: jobBoardDomainParams,
  },
  {
    regex: /^\/jobs\/(?<target_slug>(?!saved$|portal$)[^/]+)\/?$/,
    ...JOB_BOARD_VIEW,
    target_type: 'job',
    condition: isJobBoardDomainPage,
    params: jobBoardDomainParams,
  },
  {
    regex: /^\/jobs\/(?<target_slug>[^/]+)\/apply\/?$/,
    ...JOB_BOARD_VIEW,
    type: 'apply',
    target_type: 'job',
    condition: isJobBoardDomainPage,
    params: jobBoardDomainParams,
  },
];

export const startView = (prevLocation, currLocation) => {
  const event = find(EVENTS, (def) => {
    const match = currLocation.pathname.match(def.regex);
    const customCondition = isFunction(def.condition)
      ? def.condition(def)
      : true;

    return match && customCondition;
  });

  if (!event) {
    return null;
  }

  const prevMatch = prevLocation.pathname.match(event.regex);
  const currMatch = currLocation.pathname.match(event.regex);

  if (prevMatch && isEqual(prevMatch.groups, currMatch.groups)) {
    return null;
  }

  const params = {
    ...currMatch.groups,
    ...(isFunction(event.params) ? event.params() : {}),
  };

  return {
    ...event,
    ...params,
    ...(event.public ? { user_anonymous: visitorId(params) } : {}),
    ...(event.public && navigator.webdriver ? { bot: 'webdriver' } : {}),
    user_agent: navigator.userAgent,
    path: currLocation.pathname,
  };
};

export const endView = (event, prevLocation, currLocation) => {
  const prevMatch = prevLocation.pathname.match(event.regex);
  const currMatch = currLocation.pathname.match(event.regex);

  if (prevMatch && currMatch && isEqual(prevMatch.groups, currMatch.groups)) {
    return false;
  }

  return true;
};

export const eventPayload = (event) => omit(event, DEFINITION_KEYS);
